import { type QueryKey, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import type { AssistantAvatarConfig } from "../components/UIToolkit/AssistantAvatar/assistantAvatarConfig";
import { useAuth } from "../contexts/AuthProvider";
import { useAnalytics } from "../contexts/analytics/hooks";
import { useHospitalData } from "../contexts/HospitalDataProvider";
import { backendService } from "../services/backend.service";
import type { AnnieAvatarSaveRequest, AnnieAvatarSettings } from "../utils/annieStream";
import { extractAnnieError } from "./annieErrors";

/** Keyed by user, not department: the design belongs to the signed-in user, and an account switch must not reuse it. */
export const annieAvatarKeys = {
	all: ["annieAvatar"] as const,
	user: (userId: string) => ["annieAvatar", userId] as const,
} as const;

/** Only the user's own writes change it, and each one writes the server response back into the cache. */
const AVATAR_STALE_MS = Number.POSITIVE_INFINITY;

export const ANNIE_AVATAR_ERROR_KEYS = {
	save: "annie.avatar.error.save",
	reset: "annie.avatar.error.reset",
	visibility: "annie.avatar.error.visibility",
} as const;

export const ANNIE_DEFAULT_NAME_KEY = "annie.name.default";

type AvatarOperation = keyof typeof ANNIE_AVATAR_ERROR_KEYS;

interface AvatarMutationContext {
	previous: AnnieAvatarSettings | undefined;
}

interface OptimisticAvatarMutationOptions<TVariables> {
	queryKey: QueryKey;
	operation: AvatarOperation;
	request: (variables: TVariables) => Promise<AnnieAvatarSettings>;
	applyOptimistic: (previous: AnnieAvatarSettings, variables: TVariables) => AnnieAvatarSettings;
}

function useOptimisticAvatarMutation<TVariables>({ queryKey, operation, request, applyOptimistic }: OptimisticAvatarMutationOptions<TVariables>) {
	const queryClient = useQueryClient();
	const { showErrorSnackbar } = useHospitalData();
	const { track, trackError } = useAnalytics();
	const { t } = useTranslation();

	return useMutation<AnnieAvatarSettings, unknown, TVariables, AvatarMutationContext>({
		mutationFn: request,
		onMutate: async (variables) => {
			track(`annie_avatar_${operation}_start`);
			await queryClient.cancelQueries({ queryKey });
			const previous = queryClient.getQueryData<AnnieAvatarSettings>(queryKey);
			// Nothing to patch before the first load; the server response still lands in onSuccess.
			if (previous) queryClient.setQueryData<AnnieAvatarSettings>(queryKey, applyOptimistic(previous, variables));
			return { previous };
		},
		onError: (error, _variables, context) => {
			if (context?.previous) queryClient.setQueryData<AnnieAvatarSettings>(queryKey, context.previous);
			const { status, key } = extractAnnieError(error);
			trackError(`annie_avatar_${operation}_error`, { status, message_key: key });
			showErrorSnackbar(t(ANNIE_AVATAR_ERROR_KEYS[operation]), { operation }, error);
		},
		onSuccess: (data) => {
			queryClient.setQueryData<AnnieAvatarSettings>(queryKey, data);
			track(`annie_avatar_${operation}_success`);
		},
	});
}

export interface AnnieAvatarState {
	/** undefined until the first load succeeds; a failed refetch keeps the last loaded settings. */
	settings: AnnieAvatarSettings | undefined;
	/** The design to render, or null to render the default Annie UI (never loaded, hidden, or never designed). */
	visibleConfig: AssistantAvatarConfig | null;
	isHidden: boolean;
	/** The user's name for Annie, or the standard localized "Annie" (never renamed, or not loaded yet). */
	displayName: string;
	isLoading: boolean;
	isError: boolean;
	refetch: () => void;
	/** One write for the design and the name. `onSuccess` runs once the server has both (the Studio closes then, and stays open on an error). */
	saveAvatar: (request: AnnieAvatarSaveRequest, options: { onSuccess: () => void }) => void;
	/** `onSuccess` runs once the server has dropped the design, so a failed reset leaves the Studio's draft on the saved one. */
	resetAvatar: (options: { onSuccess: () => void }) => void;
	setHidden: (hidden: boolean) => void;
	isSaving: boolean;
	isResetting: boolean;
	isSettingHidden: boolean;
}

/** Every mounted consumer sees the same failure; its shared `errorUpdatedAt` makes it tracked once. */
let lastTrackedLoadErrorAt = 0;

const useAnnieAvatarQuery = (queryKey: QueryKey, enabled: boolean) => {
	const { trackError } = useAnalytics();
	const query = useQuery({
		queryKey,
		queryFn: async () => (await backendService.getAnnieAvatar()).data,
		enabled,
		staleTime: AVATAR_STALE_MS,
		refetchOnWindowFocus: false,
	});
	const { error, errorUpdatedAt } = query;

	useEffect(() => {
		if (!error || errorUpdatedAt === lastTrackedLoadErrorAt) return;
		lastTrackedLoadErrorAt = errorUpdatedAt;
		const { status, key } = extractAnnieError(error);
		trackError("annie_avatar_load_error", { status, message_key: key });
	}, [error, errorUpdatedAt, trackError]);

	return query;
};

const useDisplayName = (settings: AnnieAvatarSettings | undefined): string => {
	const { t } = useTranslation();
	// DEFAULT: never renamed (or not loaded yet) shows the standard name, the same one other people see.
	return settings?.name ?? t(ANNIE_DEFAULT_NAME_KEY);
};

/** Just Annie's name, for surfaces that show it but never write the settings. */
export const useAnnieDisplayName = (): string => {
	const { userInfo } = useAuth();
	const { data: settings } = useAnnieAvatarQuery(annieAvatarKeys.user(userInfo.id), !!userInfo.id);
	return useDisplayName(settings);
};

export const useAnnieAvatar = (): AnnieAvatarState => {
	const { userInfo } = useAuth();
	const userId = userInfo.id;
	const queryKey = annieAvatarKeys.user(userId);
	const { data: settings, isLoading, isError, refetch: refetchQuery } = useAnnieAvatarQuery(queryKey, !!userId);
	const displayName = useDisplayName(settings);
	const refetch = useCallback(() => void refetchQuery(), [refetchQuery]);

	const save = useOptimisticAvatarMutation<AnnieAvatarSaveRequest>({
		queryKey,
		operation: "save",
		request: async (request) => (await backendService.putAnnieAvatar(request)).data,
		applyOptimistic: (previous, { avatar, name }) => ({ ...previous, avatar, name }),
	});
	const reset = useOptimisticAvatarMutation<void>({
		queryKey,
		operation: "reset",
		request: async () => (await backendService.deleteAnnieAvatar()).data,
		applyOptimistic: (previous) => ({ ...previous, avatar: null }),
	});
	const visibility = useOptimisticAvatarMutation<boolean>({
		queryKey,
		operation: "visibility",
		request: async (hidden) => (await backendService.putAnnieAvatarVisibility(hidden)).data,
		applyOptimistic: (previous, hidden) => ({ ...previous, hidden }),
	});
	const { mutate: mutateReset } = reset;
	const resetAvatar = useCallback((options: { onSuccess: () => void }) => mutateReset(undefined, options), [mutateReset]);

	return {
		settings,
		visibleConfig: settings && !settings.hidden ? settings.avatar : null,
		// DEFAULT: unloaded reads as not hidden; harmless because visibleConfig is already null until settings load.
		isHidden: settings?.hidden === true,
		displayName,
		isLoading,
		isError,
		refetch,
		saveAvatar: save.mutate,
		resetAvatar,
		setHidden: visibility.mutate,
		isSaving: save.isPending,
		isResetting: reset.isPending,
		isSettingHidden: visibility.isPending,
	};
};
